import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import socket from "../../components/ScoreboardAdmin/socketClient";
import { getCompetitionById } from "../../services/competitionsServices";
import {
  calculateNeWazaResult,
  createEmptyNeWazaScore,
  getNeWazaPoints,
  isDisqualified,
} from "../../utils/neWazaScoring";

/* -------------------------------------------------------------------------- */
/* Helpers                                                                    */
/* -------------------------------------------------------------------------- */

const safeNumber = (value, fallback = 0) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

const formatChrono = (seconds) => {
  const total = Math.max(0, safeNumber(seconds, 0));
  const mins = String(Math.floor(total / 60)).padStart(2, "0");
  const secs = String(total % 60).padStart(2, "0");
  return `${mins}:${secs}`;
};

const getCompetiteurDisplayName = (competiteur, fallback = "Combattant") => {
  if (!competiteur) return fallback;

  const nom = String(
    competiteur.nom_competiteur || competiteur.nom || "",
  ).trim();
  const prenom = String(
    competiteur.prenom_competiteur || competiteur.prenom || "",
  ).trim();

  const full = `${nom.toUpperCase()} ${prenom}`.trim();
  return full || fallback;
};

const getCompetiteurClub = (competiteur) => {
  if (!competiteur) return "";
  return String(
    competiteur.club_competiteur ||
      competiteur.club ||
      competiteur.nom_club ||
      "",
  ).trim();
};

const getFightCompetitor = (fight, side = 1) => {
  if (!fight) return null;

  if (side === 1) {
    return (
      fight.c1 ||
      fight.competiteur1 ||
      fight.red ||
      fight.redCompetiteur ||
      null
    );
  }

  return (
    fight.c2 ||
    fight.competiteur2 ||
    fight.blue ||
    fight.blueCompetiteur ||
    null
  );
};

const normalizeNeWazaPayload = (payload) => {
  const source = payload?.data ?? payload ?? {};
  const empty = createEmptyNeWazaScore();

  return {
    discipline: source.discipline || "Ne-Waza",

    competiteur1:
      source.competiteur1 || source.redCompetiteur || source.red || null,
    competiteur2:
      source.competiteur2 || source.blueCompetiteur || source.blue || null,

    redScore: { ...empty, ...(source.redScore || source.score1 || {}) },
    blueScore: { ...empty, ...(source.blueScore || source.score2 || {}) },

    chrono: Math.max(
      0,
      safeNumber(source.chrono ?? source.timeLeft ?? source.remainingTime, 0),
    ),

    chronoMax: Math.max(
      0,
      safeNumber(source.chronoMax ?? source.dureeCombat ?? source.duration, 0),
    ),

    chronoMode:
      String(source.chronoMode || "").toLowerCase() === "countup"
        ? "countup"
        : "countdown",

    isGoldenScore: Boolean(source.isGoldenScore),

    categorieAge:
      source.categorieAge || source.categorie_age || source.ageCategory || "",

    categoriePoids:
      source.categoriePoids ||
      source.categorie_poids ||
      source.weightCategory ||
      "",

    combatLabel:
      source.combatLabel ||
      source.phaseLabel ||
      source.phase ||
      source.roundLabel ||
      source.tableauLabel ||
      source.tourLabel ||
      "",

    winnerColor: source.winnerColor || null,
    needGoldenScore: Boolean(source.needGoldenScore),
    medicalCallActive: Boolean(source.medicalCallActive),
    videoReviewActive: Boolean(source.videoReviewActive),
    message: source.message || "",
  };
};

/* -------------------------------------------------------------------------- */
/* Waiting screen                                                             */
/* -------------------------------------------------------------------------- */

function WaitingScreen({
  competitionName,
  tapisId,
  title,
  subtitle,
  nextFight,
}) {
  const nextRed = getFightCompetitor(nextFight, 1);
  const nextBlue = getFightCompetitor(nextFight, 2);

  return (
    <div className="w-screen h-screen bg-black text-white flex flex-col items-center justify-center px-8 text-center">
      <div className="text-[clamp(2.2rem,4.4vw,4.6rem)] font-black">
        {competitionName || "Compétition"}
      </div>

      <div className="mt-4 text-[clamp(5.4rem,14vw,12.5rem)] font-black text-emerald-400 leading-none">
        TAPIS {tapisId}
      </div>

      <div className="mt-8 text-[clamp(2rem,3.8vw,4.2rem)] font-bold">
        {title}
      </div>

      {!!subtitle && (
        <div className="mt-3 text-[clamp(1.1rem,2vw,1.8rem)] text-white/70">
          {subtitle}
        </div>
      )}

      {nextFight && (
        <div className="mt-10 rounded-3xl border border-white/15 bg-white/10 px-8 py-6 backdrop-blur max-w-6xl">
          <div className="text-[clamp(1.1rem,1.9vw,1.6rem)] font-black tracking-[0.25em] text-yellow-300">
            PROCHAIN COMBAT
          </div>

          <div className="mt-4 text-[clamp(1.8rem,3.2vw,3.4rem)] font-black">
            {getCompetiteurDisplayName(nextRed, "Rouge")}
            <span className="text-white/50 mx-4">vs</span>
            {getCompetiteurDisplayName(nextBlue, "Bleu")}
          </div>
        </div>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Public row                                                                 */
/* -------------------------------------------------------------------------- */

function MiniChip({ children }) {
  return (
    <span className="inline-flex items-center rounded-full bg-black/25 px-3 py-1 text-[clamp(.8rem,1vw,1rem)] font-semibold text-white/90">
      {children}
    </span>
  );
}

function StateBadge({ children, className = "" }) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-3 py-1 text-[clamp(.8rem,1vw,1rem)] font-black ${className}`}
    >
      {children}
    </span>
  );
}

function NeWazaPublicRow({
  color = "red",
  competiteur,
  score,
  highlightWinner = false,
}) {
  const pts = getNeWazaPoints(score || {});
  const advantages = Number(score?.advantages || 0);
  const penalties = Number(score?.penalties || 0);
  const club = getCompetiteurClub(competiteur);
  const name = getCompetiteurDisplayName(
    competiteur,
    color === "red" ? "Rouge" : "Bleu",
  );

  const theme =
    color === "red"
      ? {
          wrapper: highlightWinner
            ? "bg-[linear-gradient(90deg,rgba(127,29,29,1)_0%,rgba(220,38,38,.92)_58%,rgba(239,68,68,.88)_100%)] border-red-300/40"
            : "bg-[linear-gradient(90deg,rgba(127,29,29,.96)_0%,rgba(220,38,38,.82)_58%,rgba(239,68,68,.74)_100%)] border-red-400/20",
          label: "text-red-100/85",
          scoreBox: "bg-black/20",
          winner: "bg-white text-red-700",
        }
      : {
          wrapper: highlightWinner
            ? "bg-[linear-gradient(90deg,rgba(30,58,138,1)_0%,rgba(37,99,235,.92)_58%,rgba(59,130,246,.88)_100%)] border-blue-300/40"
            : "bg-[linear-gradient(90deg,rgba(30,58,138,.96)_0%,rgba(37,99,235,.82)_58%,rgba(59,130,246,.74)_100%)] border-blue-400/20",
          label: "text-blue-100/85",
          scoreBox: "bg-black/20",
          winner: "bg-white text-blue-700",
        };

  return (
    <div className={`h-full border-b border-white/10 ${theme.wrapper}`}>
      <div className="h-full px-8 md:px-10 py-5 flex items-center justify-between gap-6">
        <div className="min-w-0 flex-1">
          <div
            className={`text-[clamp(1rem,1.4vw,1.2rem)] font-bold uppercase tracking-[0.25em] ${theme.label}`}
          >
            {color === "red" ? "Rouge" : "Bleu"}
          </div>

          <div className="mt-3 text-[clamp(2rem,3.7vw,4.2rem)] font-black leading-[1.02] break-words">
            {name}
          </div>

          {!!club && (
            <div className="mt-2 text-[clamp(1rem,1.45vw,1.4rem)] font-semibold text-white/82 truncate">
              {club}
            </div>
          )}

          <div className="mt-5 flex flex-wrap gap-2">
            <MiniChip>Avt. {advantages}</MiniChip>
            <MiniChip>Pén. {penalties}</MiniChip>

            {score?.submission && (
              <StateBadge className="bg-emerald-500 text-slate-950">
                Ippon
              </StateBadge>
            )}

            {isDisqualified(score) && (
              <StateBadge className="bg-rose-600 text-white">
                Disqualification
              </StateBadge>
            )}

            {score?.medicalStop && (
              <StateBadge className="bg-orange-500 text-slate-950">
                Médical
              </StateBadge>
            )}

            {score?.forfait && (
              <StateBadge className="bg-amber-400 text-slate-950">
                Forfait
              </StateBadge>
            )}

            {highlightWinner && (
              <StateBadge className={theme.winner}>VAINQUEUR</StateBadge>
            )}
          </div>
        </div>

        <div
          className={`shrink-0 rounded-[28px] ${theme.scoreBox} px-8 py-5 min-w-[190px] md:min-w-[240px] text-center`}
        >
          <div className="text-[clamp(.9rem,1.1vw,1rem)] uppercase tracking-[0.22em] text-white/60">
            Score
          </div>
          <div className="mt-2 text-[clamp(5rem,10vw,9rem)] font-black leading-none tabular-nums">
            {pts.total}
          </div>
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Main                                                                       */
/* -------------------------------------------------------------------------- */

export default function ScoreboardNeWazaPublic() {
  const { competitionId, tapisId } = useParams();

  const [competitionName, setCompetitionName] = useState("");
  const [combatData, setCombatData] = useState(null);
  const [isPaused, setIsPaused] = useState(false);
  const [nextFight, setNextFight] = useState(null);
  const [nextMsg, setNextMsg] = useState("");

  useEffect(() => {
    const fetchCompetition = async () => {
      try {
        const data = await getCompetitionById(competitionId);
        setCompetitionName(data?.nom_competition || "");
      } catch (error) {
        console.error("Erreur chargement compétition :", error);
      }
    };

    fetchCompetition();
  }, [competitionId]);

  useEffect(() => {
    const roomKey = `${competitionId}_${tapisId}`;
    socket.emit("join_combat", roomKey);

    const onScoreUpdate = (payload) => {
      setCombatData(normalizeNeWazaPayload(payload));
    };

    const onPause = ({ paused }) => {
      setIsPaused(!!paused);
    };

    const onNextFight = ({ nextFight, message }) => {
      setNextFight(nextFight || null);
      setNextMsg(String(message || ""));
    };

    socket.on("score_update", onScoreUpdate);
    socket.on("public_pause", onPause);
    socket.on("public_next_fight", onNextFight);

    return () => {
      socket.off("score_update", onScoreUpdate);
      socket.off("public_pause", onPause);
      socket.off("public_next_fight", onNextFight);
    };
  }, [competitionId, tapisId]);

  const computedResult = useMemo(() => {
    if (!combatData) return null;

    try {
      return calculateNeWazaResult(
        combatData.redScore || createEmptyNeWazaScore(),
        combatData.blueScore || createEmptyNeWazaScore(),
        {
          isGoldenScore: Boolean(combatData.isGoldenScore),
        },
      );
    } catch (error) {
      console.error("Erreur calcul résultat Ne-Waza public :", error);
      return null;
    }
  }, [combatData]);

  const derivedWinnerColor = useMemo(() => {
    if (!computedResult) return null;
    return computedResult.winnerColor || null;
  }, [computedResult]);

  const winnerColor = combatData?.winnerColor || derivedWinnerColor || null;

  const showGoldenBanner = useMemo(() => {
    if (!combatData) return false;
    return Boolean(
      combatData.isGoldenScore ||
        (combatData.needGoldenScore && combatData.chrono <= 0),
    );
  }, [combatData]);

  const chronoPercent = useMemo(() => {
    if (!combatData) return 0;

    if (combatData.chronoMode === "countup") {
      return 100;
    }

    if (!combatData.chronoMax) return 0;

    return Math.max(
      0,
      Math.min(100, (combatData.chrono / combatData.chronoMax) * 100),
    );
  }, [combatData]);

  const chronoColorClass = useMemo(() => {
    if (!combatData) return "text-white";
    if (combatData.chronoMode === "countup" || combatData.isGoldenScore) {
      return "text-yellow-300";
    }
    if (combatData.chrono <= 10) return "text-red-400";
    if (combatData.chrono <= 30) return "text-amber-300";
    return "text-white";
  }, [combatData]);

  const nextRed = getFightCompetitor(nextFight, 1);
  const nextBlue = getFightCompetitor(nextFight, 2);

  if (isPaused) {
    return (
      <WaitingScreen
        competitionName={competitionName}
        tapisId={tapisId}
        title="Tapis en pause"
        subtitle={nextMsg}
        nextFight={nextFight}
      />
    );
  }

  if (!combatData) {
    return (
      <WaitingScreen
        competitionName={competitionName}
        tapisId={tapisId}
        title="En attente du combat..."
        subtitle={nextMsg}
        nextFight={nextFight}
      />
    );
  }

  return (
    <div className="w-screen h-screen overflow-hidden bg-black text-white font-black flex flex-col">
      {/* Bandeau haut */}
      <div className="h-[11%] bg-black px-8 md:px-10 flex items-center justify-between border-b border-white/10">
        <div className="min-w-0">
          <div className="truncate text-[clamp(1.8rem,3.6vw,3.8rem)] font-black leading-none">
            {competitionName || "Compétition"}
          </div>
          <div className="mt-2 text-[clamp(1rem,1.8vw,1.45rem)] text-white/65 font-semibold uppercase tracking-[0.22em]">
            {combatData.discipline || "Ne-Waza"}
          </div>
        </div>

        <div className="text-right min-w-0">
          {!!nextFight && (
            <div className="truncate text-[clamp(1rem,1.7vw,1.4rem)] text-yellow-300 font-bold">
              Prochain : {getCompetiteurDisplayName(nextRed, "Rouge")} vs{" "}
              {getCompetiteurDisplayName(nextBlue, "Bleu")}
            </div>
          )}
          <div className="mt-1 text-[clamp(2.2rem,4.2vw,4.8rem)] leading-none">
            Tapis {tapisId}
          </div>
        </div>
      </div>

      {/* Ligne Rouge */}
      <div className="h-[29.5%]">
        <NeWazaPublicRow
          color="red"
          competiteur={combatData.competiteur1}
          score={combatData.redScore}
          highlightWinner={winnerColor === "red"}
        />
      </div>

      {/* Ligne Bleue */}
      <div className="h-[29.5%]">
        <NeWazaPublicRow
          color="blue"
          competiteur={combatData.competiteur2}
          score={combatData.blueScore}
          highlightWinner={winnerColor === "blue"}
        />
      </div>

      {/* Bas */}
      <div className="h-[30%] bg-black border-t border-white/10 px-8 md:px-10 py-5 grid grid-cols-3 gap-8 items-center">
        <div className="min-w-0">
          <div className="text-[clamp(2rem,4.1vw,4.6rem)] uppercase leading-none">
            {combatData.categorieAge || "Catégorie"}
          </div>

          {!!combatData.categoriePoids && (
            <div className="mt-4 text-[clamp(1.6rem,2.7vw,3.4rem)] uppercase text-white/90 leading-none">
              {combatData.categoriePoids}
            </div>
          )}

          {!!combatData.combatLabel && (
            <div className="mt-4 text-[clamp(1rem,1.5vw,1.4rem)] text-white/58 font-semibold uppercase tracking-[0.18em]">
              {combatData.combatLabel}
            </div>
          )}
        </div>

        <div className="text-center px-2">
          {showGoldenBanner ? (
            <div className="inline-flex items-center rounded-full bg-yellow-400/15 border border-yellow-300/30 px-7 py-3 text-yellow-300 text-[clamp(1.05rem,1.8vw,1.5rem)]">
              GOLDEN SCORE
            </div>
          ) : winnerColor === "red" ? (
            <div className="inline-flex items-center rounded-full bg-red-500/15 border border-red-300/25 px-7 py-3 text-red-200 text-[clamp(1.05rem,1.8vw,1.5rem)]">
              AVANTAGE ROUGE
            </div>
          ) : winnerColor === "blue" ? (
            <div className="inline-flex items-center rounded-full bg-blue-500/15 border border-blue-300/25 px-7 py-3 text-blue-200 text-[clamp(1.05rem,1.8vw,1.5rem)]">
              AVANTAGE BLEU
            </div>
          ) : (
            <div className="inline-flex items-center rounded-full bg-white/10 border border-white/15 px-7 py-3 text-white/90 text-[clamp(1.05rem,1.8vw,1.5rem)]">
              COMBAT EN COURS
            </div>
          )}

          <div className="mt-4 flex flex-wrap items-center justify-center gap-3">
            {combatData.medicalCallActive && (
              <div className="inline-flex items-center rounded-full bg-orange-500/15 border border-orange-300/30 px-5 py-2 text-orange-200 text-[clamp(.95rem,1.4vw,1.2rem)]">
                APPEL MÉDICAL
              </div>
            )}

            {combatData.videoReviewActive && (
              <div className="inline-flex items-center rounded-full bg-cyan-500/15 border border-cyan-300/30 px-5 py-2 text-cyan-200 text-[clamp(.95rem,1.4vw,1.2rem)]">
                CHALLENGE VIDÉO
              </div>
            )}
          </div>

          {!!(combatData.message || nextMsg) && (
            <div className="mt-5 text-[clamp(1rem,1.65vw,1.35rem)] text-white/78 font-semibold">
              {combatData.message || nextMsg}
            </div>
          )}
        </div>

        <div className="flex flex-col items-end justify-center">
          <div
            className={`tabular-nums leading-none text-[clamp(4.8rem,11vw,9.2rem)] ${chronoColorClass}`}
          >
            {formatChrono(combatData.chrono)}
          </div>

          <div className="mt-5 w-full max-w-[560px] h-[22px] rounded-full overflow-hidden bg-white/10">
            <div
              className={`h-full transition-all duration-300 ${
                combatData.chronoMode === "countup"
                  ? "bg-yellow-400"
                  : "bg-emerald-400"
              }`}
              style={{ width: `${chronoPercent}%` }}
            />
          </div>

          <div className="mt-3 text-[clamp(.95rem,1.45vw,1.2rem)] text-white/58 font-semibold uppercase tracking-[0.18em]">
            {combatData.chronoMode === "countup"
              ? "Temps écoulé en golden score"
              : `Temps réglementaire : ${formatChrono(combatData.chronoMax)}`}
          </div>
        </div>
      </div>
    </div>
  );
}